import React, { useState, useRef, useEffect } from 'react';
import { Cog6ToothIcon, UserIcon, ArrowRightOnRectangleIcon, KeyIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services';

interface AvatarDropdownProps {
  onConfigClick: () => void;
  onChangePasswordClick: () => void;
}

const AvatarDropdown: React.FC<AvatarDropdownProps> = ({ onConfigClick, onChangePasswordClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
  };

  const handleConfigClick = () => {
    onConfigClick();
    setIsOpen(false);
  };

  const handleChangePasswordClick = () => {
    onChangePasswordClick();
    setIsOpen(false);
  };

  // Check if user has admin role
  const isAdmin = () => {
    if (!user) return false;
    const userRoles = authService.getUserRoles(user);
    return userRoles.includes('admin');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
      >
        {(user as any)?.user_name?.charAt(0).toUpperCase() || 'U'}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
          <div className="py-1">
            {/* User Info */}
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900">{user?.name || (user as any)?.user_name}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>

            {/* Profile */}
            <button
              onClick={() => setIsOpen(false)}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <UserIcon className="h-4 w-4" />
              Thông tin cá nhân
            </button>

            {/* Change Password */}
            <button
              onClick={handleChangePasswordClick}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <KeyIcon className="h-4 w-4" />
              Đổi mật khẩu
            </button>

            {/* Configuration - Only show for admin users */}
            {isAdmin() && (
              <button
                onClick={handleConfigClick}
                className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <Cog6ToothIcon className="h-4 w-4" />
                Cấu hình
              </button>
            )}

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
              Đăng xuất
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvatarDropdown;
